import type { Languages } from '@framework/constants/enums/languages';
import type { MeetingDocumentPreReadingSettings } from '@framework/constants/enums/meetingDocumentPreReadingPreferences';

import { type DocumentRevision } from '@documents/types';

import type { CreatedVia } from '@meetings/details/agenda/agenda.constants';
import type { ConflictingUpdateType } from '@meetings/details/agenda/types';
import { type MeetingParticipant } from '@meetings/details/details/components/DetailsForm/FormFields/MeetingParticipants/MeetingParticipants.type';
import { type AddToAgendaPermissionsItem } from '@meetings/types';

import type { UPDATE_PERMISSIONS } from '@tasksDecisions/tasksDecisions.constants';
import {
    type TaskDecisionType,
    type TasksDecisionsState
} from '@tasksDecisions/tasksDecisions.types';

import type { AccessManagementPermission } from '@components/layout/AccessManagement/AccessManagement.interface';

import type { ThreadStates } from '@type/comments';
import { type Committee } from '@type/common';
import type { MEMBER_STATES, Member } from '@type/members';

export enum CustomPermissionsInfo {
    DEACTIVATED_MEMBER = 'deactivated_member',
    EDITOR_OF_MEETING = 'editor_of_meeting',
    INSUFFICIENT_CLASSIFICATION_LEVEL = 'insufficient_classification_level',
    NO_ACCESS_TO_PARENT = 'no_access_to_parent',
    NO_ACCESS_TO_VOTING = 'no_access_to_voting'
}

type Contributor = {
    email?: string;
    memberId?: string;
    type: string;
};

export type Contribution = {
    contributor: Contributor;
    created: string;
    id: string;
    mimeType: string;
    name: string;
};

export type AgendaItemRequest = {
    id: string;
    requestedByLabel: string;
    topic?: {
        id: string;
        name: string;
        totalAgendaItemRequests: number;
    };
};

export type MeetingDocument = {
    id: string;
    level: number;
    depth: number;
    parentId: string;
    file: string;
    name: string;
    order: number;
    createdVia?: CreatedVia;
    originId: string | null;
    created: string;
    modified: string;
    labels: string[];
    mimeType: string;
    revisions: DocumentRevision[];
    thumbnail: string;
    pageCount: number;
    letterLabel: string;
    serviceState: string;
    allowedViewers: string[];
    lastAnnotation: string;
    allowedAttendees: string[];
    classificationLevel: number;
    hasUniquePermissions: boolean;
    contribution: Contribution;
    editableAttendees: string[];
    editableViewers: string[];
    customPermissionsInfo: { [key: string]: string };
    externalContentSource: string | null;
    isDocument: boolean | null;
    isLinkContent?: boolean | null;
    isOutdatedDocument: boolean;
    isPasswordProtected: boolean;
    preReadingPreference?: MeetingDocumentPreReadingSettings | null;
};

export type Topic = {
    agendaItemRequest: AgendaItemRequest | null;
    agendaNumber: string;
    agendaTime: string;
    allowedAttendees: string[];
    allowedGuests?: string[];
    allowedMembers: null | string[];
    allowedMinutesViewers?: string[];
    allowedToVote: boolean;
    allowedViewers: string[];
    allowedVoteDecisions: number[];
    allowedVoters?: string[];
    category: number;
    classificationLevel: number;
    committeeRun: Committee[] | null;
    contributors: ContributionRequest[];
    customPermissionsInfo: { [key: string]: CustomPermissionsInfo };
    customVotersPermissionsInfo?: { [key: string]: CustomPermissionsInfo };
    allCommentsCount: number;
    depth: number;
    documents: any[];
    duration: number;
    fromDraft: string | null;
    editableAttendees: string[];
    editableViewers: string[];
    editableMembers: string[];
    hasUniquePermissions: boolean;
    id: string;
    isOutdatedDocument?: boolean | null;
    isMasked: boolean;
    isModified: boolean;
    isNewlyCreated: boolean;
    isPublished: boolean;
    isAnyoneExcluded?: boolean;
    isAnyNonGuestExcluded?: boolean;
    level: number;
    modified: string;
    name: string;
    order: number;
    origin: number | null;
    presenter: string;
    remarks: string;
    startsAt: string;
    topics: Topic[];
    unreadCommentsCount: number;
    votingDecision: number;
    votingDecisionReason: null;
    votingEnabledBefore?: boolean;
    votingEnabledDuring?: boolean;
    votingEnabledAfter?: boolean;
    votingSummary: VotingSummary;
    votingStatus: VotingStatus;
    parentId?: string | undefined;
    originId?: string | null;
    created?: string;
    createdVia?: CreatedVia;
    replacedAt?: string | null;
};

export type VotingTimeframeSettings = {
    votingEnabledAfter?: boolean;
    votingEnabledBefore?: boolean;
    votingEnabledDuring?: boolean;
};

export enum VotingStatus {
    CLOSED = 0,
    OPENED = 1
}

export type Vote = {
    admin: string | null;
    decision: number;
    ipAddress: string | null;
    justification: string | null;
    member: string;
    votedAt: string | null;
    isVotingOnBehalfAllowed?: boolean;
};

export type VotingSummary = {
    summary: { [key: string]: number };
    summaryPercent: { [key: number]: string };
    votes: Vote[];
};

export type ContributionRequest = {
    agendaItem: {
        id: string;
        name: string;
        category: number;
    };
    contributions: Contribution[];
    contributor: {
        type: string;
        [key: string]: string;
    };
    created: string;
    dueDate: string;
    event: {
        id: string;
        name: string;
        start: string;
        end: string;
        timezone: string;
    };
    expiresAt: string;
    hasDueDateReminder: boolean;
    id: string;
    lastNotification: {
        created: string;
        id: string;
        message: string;
        sender: {
            id: string;
            name: string;
        };
    };
    message: string;
    modified: string;
    roomId: string;
    state: string;
};

export type Comment = {
    created: string;
    id: string;
    member: Member;
    modified: string;
    text: string;
    threadId: string;
    unread: boolean;
};

export type Comments = {
    agendaItemId: string;
    agendaItemName?: string | null;
    agendaItemNumber?: string | null;
    comments: Comment[];
    commentsCount: number;
    created: string;
    eventId: string;
    id: string;
    meetingDocumentId: string | null;
    meetingDocumentPage: number | null;
    meetingDocumentPageX: number | null;
    meetingDocumentPageY: number | null;
    meetingDocumentName?: string | null;
    meetingDocumentLetter?: string | null;
    meetingDocumentPinpointNumber?: number | null;
    modified: string;
    permissions: {
        isPublic: boolean;
        allowedMembers: string[];
    };
    roomId: string;
    state: ThreadStates;
    unreadCommentsCount: number;
};

export type Viewers = {
    groups: string[];
    members: string[];
};

type GroupMembers = { [key: string]: string };

export enum ConferenceCallProvider {
    custom = 'custom',
    inPerson = 'in_person',
    msTeams = 'ms_teams',
    none = '',
    sherpanyMeet = 'sherpany_meet'
}

export type AgendaTemplateTopic = {
    name: string;
    topics: AgendaTemplateTopic[];
};

export type AgendaTemplate = {
    id: string;
    name: string;
    topics: AgendaTemplateTopic[];
};

export enum UploadInvitationStatus {
    Default = 'default',
    Disabled = 'disabled',
    Done = 'done',
    Error = 'error',
    Invalid = 'invalid',
    Progress = 'progress',
    Success = 'success'
}

export type PublishingNote = string;

type LastPublishingNote = {
    content: PublishingNote;
    created: string;
    id: string;
};

export enum PublishingState {
    Approval = 'published_approval',
    Draft = 'published_draft',
    Ready = 'published_ready',
    UnpublishedApproval = 'unpublished_approval',
    UnpublishedDraft = 'unpublished_draft',
    UnpublishedReady = 'unpublished_ready',
    UnpublishedVoting = 'unpublished_voting',
    Voting = 'published_voting'
}

export type Attendance = {
    created: string;
    eventId: string;
    id: string;
    isDraft: boolean;
    meetingTitle: string;
    memberId: string;
    modified: string;
    roomId: string;
    state: string;
};

type Signer = {
    memberId: string;
    hasSigned: boolean;
    signedAt: string | null;
};

export enum SignatureRequestSigningService {
    Docusign = 'docusign',
    PSPDFKit = 'pspdfkit'
}

export type SignatureRequest = {
    dueAt?: string;
    message?: string;
    notificationsSentAt?: string | null;
    requester?: {
        memberId: string;
    };
    signers?: Array<Signer>;
    agendaItems?: Array<{
        agendaItemId: string;
    }>;
    documents?: string[];
    signingService?: SignatureRequestSigningService;
};

type UnpublishedChanges = {
    details: boolean;
};

enum MeetingState {
    NotPublished = 'not published',
    Published = 'published',
    Republish = 'republish'
}

type MeetingFeedbackForm = {
    id: string;
    deeplink: string;
    unreadAnswers: number;
    totalAnswers: number;
    feedbackSubmitted: boolean;
    isSurveyFeedbackRequired?: boolean;
};

export type AgendaMinutesVisibility = {
    memberId: string;
    canRead: boolean;
    approval?: {
        restart: boolean;
        status: 'not_requested' | 'pending' | 'unanswered' | 'approved';
        approvedAt: string;
    };
};

type MeetingDynamicParticipation = {
    end: string;
    start: string;
};

export type Meeting = {
    agendaActionLoading: boolean;
    agendaActionType: string;
    agendaApprovals: Approval[];
    agendaApprovalRequested: boolean;
    agendaApproved: boolean;
    agendaEnabled: boolean;
    agendaLastModifiedAt: string;
    agendaState: PublishingState;
    agendaVisibility?: AgendaMinutesVisibility[];
    unpublishedChanges: UnpublishedChanges;
    agendaTemplates: AgendaTemplate[];
    showAgendaTemplates: boolean;
    canEdit?: boolean;
    agendaViewers: Viewers;
    allAttendees: string[];
    allEditors: string[];
    allViewers: string[];
    attendance: Attendance[];
    attendees: string[];
    attendeesGroups: string[];
    attendeesGroupsMembers: GroupMembers;
    boardbookPublished: string;
    canSeePersonalPreparedness?: boolean;
    canSeeOverallPreparedness?: boolean;
    commentsCount: number;
    conferenceCall: string;
    conferenceCallProvider?: ConferenceCallProvider;
    coverPageEnabled: boolean;
    created: string;
    deadline: string;
    deadlineUTC: string;
    defaultDuration: number;
    defaultSecurity: number;
    deleted: boolean;
    documents: any[];
    dominantLanguage?: Languages | null;
    dynamicParticipation?: MeetingDynamicParticipation[];
    contributionRequestModalVisibility: boolean;
    editedName?: string;
    editors: string[];
    editorsGroups: string[];
    editorsGroupsMembers: GroupMembers;
    end: string;
    endUTC: string;
    endTime: string;
    eventType: number;
    expandedMinutesItem: string;
    flatTopics: TransformedTopic[];
    hasAgendaLevelRestriction?: boolean;
    hasAllResolutionConfirmed: boolean;
    hasDiscussion: boolean;
    hasCheckIn: boolean;
    hasResolutionToConfirm: boolean;
    hasMinutesPageView: boolean;
    id: string;
    includeDocumentsInMinutesPdf: boolean;
    information: string;
    invitationDocument: string;
    invitationDocumentCreated: string;
    invitationDocumentName: string;
    invitationThumbnail: string;
    invitationUploaded: boolean;
    isAgendaSelectivelyPublished: boolean;
    lastPublished: string;
    lastPublishingNote: LastPublishingNote;
    location: string;
    meetingFeedbackForm: MeetingFeedbackForm | null;
    meetingLead: string | null;
    ms365TaskId?: string;
    members: MeetingParticipant[];
    membersSmartList: MembersSmartList;
    minutes: MeetingMinutes[];
    minutesApprovals: Approval[];
    minutesApprovalRequested: boolean;
    minutesApproved: boolean;
    minutesLastPublished: string;
    minutesPublishingNote: string;
    minutesState: PublishingState;
    minutesViewers: Viewers;
    minutesVisibility?: AgendaMinutesVisibility[];
    modified: string;
    msteamsEvent?: MSTeamsEvent | null;
    name: string;
    nextPublishAgendaViewers: string[];
    owner?: string;
    permission?: AccessManagementPermission;
    publishOn: string;
    roomId: string;
    selectedAgendaItem: {
        id: string;
    };
    sendCalendarInvitation?: boolean;
    shortLink?: string | null;
    start: string;
    startTime: string;
    startUTC: string;
    state: MeetingState | string;
    timezone: string;
    topics: Topic[];
    unreadCommentsCount: number;
    version: number;
    viewers: string[];
    viewersGroups: string[];
    viewersGroupsMembers: GroupMembers;
    voteRequestStatus: VoteRequestStatus | null;
    minutesDigitalSignatureRequest: SignatureRequest;
    enableMeetingFeedbackForm: boolean;
    ui: {
        outdatedAgendaModal: {
            outdatedAgendaModalVisible: boolean;
        };
        conflictingAgendaUpdateModal: {
            conflictingAgendaUpdateModalVisible: boolean;
            conflictingMember: Member | null;
            type: ConflictingUpdateType;
        };
        cloneMeetingModal: {
            cloneMeetingModalVisible: boolean;
        };
        draftModalVisible: boolean;
        meetingCheckInWarning: {
            warningBarVisible: boolean;
            checkInSuggestionModal: boolean;
        };
        meetingDocumentPreReadingModal: {
            meetingDocumentPreReadingModalVisible: boolean;
        };
        minutesTranscriptsSidePanel: {
            minutesTranscriptsSidePanelOpen: boolean;
        };
        details: {
            instanceViewId: string;
        };
        info: {
            isNewlyCreated: boolean;
        };
    };
};

export type MeetingTime = {
    endTime: string;
    startTime: string;
};

export type MSTeamsEvent = {
    areCreatorCredentialsPresent: boolean;
    eventId: string;
    hasTranscriptPermission?: boolean;
};

export type TransformedTopic = Topic & {
    canHaveChildren?: boolean;
    depth: number;
    letterLabel?: string;
    isDocument?: boolean;
    isLinkContent?: boolean;
    isDeleted?: boolean;
    isOutdatedDocument?: boolean | null | '';
    parentId?: string;
    previousSibling: string | null;
    isNewlyCreated: boolean;
    votingEnabledAfter?: boolean;
    votingEnabledDuring?: boolean;
    votingEnabledBefore?: boolean;
    externalContentSource: string | null;
    preReadingPreference: MeetingDocumentPreReadingSettings | null;
    mimeType?: string;
};

export type AtomicTopic = Pick<
    TransformedTopic,
    'id' | 'name' | 'category' | 'isNewlyCreated' | 'level' | 'depth' | 'parentId' | 'topics'
>;

type MembersSmartList = {
    members: Member[];
    suggestedMembers: Member[];
};

type Approval = {
    member: string;
    event: string;
    isApproved: boolean;
};

export type AgendaItemEvent = {
    end: string;
    id: string;
    minutesState: string;
    name: string;
    start: string;
    timezone: string;
};

export type AgendaItem = {
    event: AgendaItemEvent;
    id: string;
    name: string;
};

export type MinuteNote = {
    id: string;
    roomId: string;
    agendaItemId: string;
    agendaItem: AgendaItem;
    text: string;
    created: string;
    modified: string;
    isDraft: boolean;
    isCopilotGenerated?: boolean;
};

export type InternalAssignee = {
    memberId: string;
    name: string;
    type: string;
    member: Member;
};

export type ExternalAssignee = {
    email: string;
    name?: string | null;
    type: string;
};

export type MinuteDecision = {
    agendaItem: AgendaItem;
    agendaNumber?: string;
    agendaItemId: string;
    assignee: InternalAssignee | null;
    canEditStatus: boolean;
    created: string;
    createdBy?: {
        user: {
            id: string;
            name: string;
        };
    };
    expectedOutcome: string | null;
    hasMeetingAccess: boolean | null;
    id: string;
    meetingId: string;
    isDraft: boolean;
    isEditable: boolean;
    modified: string;
    order: number;
    reviewAt: string;
    roomId: string;
    state: TasksDecisionsState;
    text: string;
    type: TaskDecisionType;
    date?: string;
    dueOn: Date | null | string;
    publishedMinuteId: string | null;
    outcome: string;
    fromDraftId: string;
    isAlreadyAttached?: boolean;
    latestStateChangeText?: string | null;
    hasPendingChanges: boolean;
    updatePermissions: UPDATE_PERMISSIONS;
    reviewOn: Date | null | string;
};

export type MinuteTask = {
    id: string;
    roomId: string;
    agendaItemId: string;
    agendaItem: AgendaItem;
    agendaNumber?: string;
    text: string;
    description?: string;
    state: TasksDecisionsState;
    stateChangeExplanation?: string;
    assignee: InternalAssignee | null;
    dueAt: string;
    dueOn: Date | null | string;
    order: number;
    created: string;
    createdBy?: {
        user: {
            id: string;
            name: string;
        };
    };
    modified: string;
    isDraft: boolean;
    isEditable: boolean;
    canEditStatus: boolean;
    hasMeetingAccess: boolean | null;
    type: TaskDecisionType;
    date?: string;
    publishedMinuteId: string | null;
    outcome: string;
    fromDraftId: string;
    isAlreadyAttached?: boolean;
    latestStateChangeText?: string | null;
    hasPendingChanges: boolean;
    updatePermissions: UPDATE_PERMISSIONS;
    reviewOn: Date | null | string;
};

export type MeetingMinutes = {
    agendaItemId: string;
    decisions: MinuteDecision[];
    note: MinuteNote;
    tasks: MinuteTask[];
};

export enum MemberType {
    Attendees = 'Attendees',
    Editors = 'Editors'
}

export type ExistingVoter = {
    decision: number;
    displayRole: string;
    firstName: string;
    id: string;
    name: string;
    profilePicture: string;
    state: MEMBER_STATES;
    isVotingOnBehalfAllowed: boolean;
};

export enum DeepLinks {
    TASK = 'task'
}

export enum VoteRequestStatus {
    PENDING = 'pending',
    SUCCESS = 'success'
}

export type MeetingFormValues = Meeting & {
    enableMeetingFeedbackForm: boolean;
    addToAgendaPermissions: AddToAgendaPermissionsItem[];
};

export type MeetingFormValue<T> = T[keyof T];
export type MeetingFormField = keyof MeetingFormValues;

export type PayloadTaskRequestDetails = Pick<
    MinuteTask,
    | 'id'
    | 'roomId'
    | 'text'
    | 'description'
    | 'assignee'
    | 'dueAt'
    | 'state'
    | 'stateChangeExplanation'
> & {
    assignee: string;
};

export type PayloadDecisionRequestDetails = Pick<
    MinuteDecision,
    'id' | 'text' | 'reviewAt' | 'state'
> & {
    assignee: string;
    email?: string;
};

export enum ItemType {
    GROUP_PARTICIPANT,
    MEMBER_PARTICIPANT,
    GROUP,
    MEMBER
}

export type AssigneeType = InternalAssignee | ExternalAssignee | null;

export type UpdateAttachedTaskPayload = {
    id: string;
    agendaItemId?: string;
    text?: string;
    description?: string;
    assignee?:
        | Pick<InternalAssignee, 'type' | 'memberId'>
        | Pick<ExternalAssignee, 'type' | 'email'>
        | null;
    dueDate?: Date | string | null;
    currentState?: TasksDecisionsState;
    type?: string;
    changeStatusData?: {
        state: TasksDecisionsState;
        stateChangeExplanation?: string;
    };
};

export enum GuestOption {
    BULK_ACTION = 'bulk_action',
    COLLAPSED_AGENDA_ITEM = 'collapsed_agenda_item',
    DETAILS_TAB = 'details_tab',
    EXPANDED_AGENDA_ITEM = 'expanded_agenda_item'
}

export type MeetingAttendanceMembers = {
    id: string;
    attendeeId: string;
    firstName: string;
    name: string;
    profilePicture: string;
    state: string;
    meetingTitle: string;
    memberState?: MEMBER_STATES;
};
